import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek } from "date-fns";
import { es } from "date-fns/locale";
import { Users, CalendarDays, CalendarCheck } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { listPatients } from "@/lib/api/patients";
import { listProfessionals } from "@/lib/api/professionals";
import { listVisits } from "@/lib/api/visits";

export const Route = createFileRoute("/dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard · Rafael González" },
      { name: "description", content: "Resumen de actividad de la clínica: citas de hoy, pacientes y profesionales." },
    ],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: () => listPatients() });
  const { data: professionals = [] } = useQuery({ queryKey: ["professionals"], queryFn: () => listProfessionals() });
  const { data: visits = [] } = useQuery({ queryKey: ["visits"], queryFn: () => listVisits() });

  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

  const todayVisits = visits.filter((v) => {
    const start = new Date(v.start);
    return start >= todayStart && start <= todayEnd;
  });
  const weekVisits = visits.filter((v) => {
    const start = new Date(v.start);
    return start >= weekStart && start <= weekEnd;
  });

  const upcomingToday = todayVisits
    .filter((v) => v.status === "scheduled")
    .sort((a, b) => a.start.localeCompare(b.start));

  const patientById = Object.fromEntries(patients.map((p) => [p.id, p]));
  const profById = Object.fromEntries(professionals.map((p) => [p.id, p]));

  const kpis = [
    { label: "Pacientes", value: patients.length, icon: Users },
    { label: "Citas hoy", value: todayVisits.length, icon: CalendarCheck },
    { label: "Citas esta semana", value: weekVisits.length, icon: CalendarDays },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Buenos días</h1>
        <p className="text-sm text-muted-foreground">
          Resumen de la clínica · {format(now, "EEEE d 'de' MMMM", { locale: es })}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
              <k.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Próximas citas de hoy</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingToday.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay citas pendientes para hoy.</p>
          ) : (
            <ul className="divide-y">
              {upcomingToday.map((v) => {
                const patient = patientById[v.patientId];
                const prof = profById[v.professionalId];
                return (
                  <li key={v.id} className="flex items-center justify-between gap-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-14 text-sm font-medium">{format(new Date(v.start), "HH:mm")}</div>
                      <div>
                        <Link
                          to="/pacientes/$id"
                          params={{ id: v.patientId }}
                          className="text-sm font-medium hover:underline"
                        >
                          {patient ? `${patient.firstName} ${patient.lastName}` : "Paciente"}
                        </Link>
                        <div className="text-xs text-muted-foreground">{v.reason}</div>
                      </div>
                    </div>
                    {prof && (
                      <Badge variant="outline" className="gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: prof.color }} />
                        {prof.firstName} {prof.lastName}
                      </Badge>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
